import { useList } from '../lib/useList.ts'
import { formatDateTime } from '../lib/format.ts'
import { PageHeader } from '../components/AppShell.tsx'
import { Button, Empty, Input, SkeletonRows } from '../components/ui.tsx'
import { IconSearch } from '../components/icons.tsx'

type AuditRow = {
  id: string
  action: string
  entity: string | null
  actor: { id: string | null; name: string | null; email: string | null }
  ip: string | null
  createdAt: string
}

// Human wording for the machine action codes. Anything unmapped falls back
// to the raw code rather than being hidden.
const LABEL: Record<string, string> = {
  'auth.login': 'Signed in',
  'auth.logout': 'Signed out',
  'auth.login_denied': 'Sign-in refused',
  'staff.create': 'Added a staff member',
  'staff.update': 'Edited a staff member',
  'staff.delete': 'Removed a staff member',
  'staff.seed': 'Seeded the first owner',
  'patient.create': 'Registered a patient',
  'patient.update': 'Edited a patient',
  'patient.delete': 'Removed a patient',
}

export function AuditPage() {
  const list = useList<AuditRow>('/api/audit')

  return (
    <>
      <PageHeader title="Audit log" />
      <div className="content">
        <div className="page-max stack" style={{ gap: 'var(--s-5)' }}>
          <p className="muted" style={{ maxWidth: '62ch' }}>
            Every change, and every refused sign-in. The database rejects edits
            and deletions on this table, so what is here cannot be rewritten —
            including by this application.
          </p>

          <div className="toolbar">
            <div className="search">
              <IconSearch className="ic" />
              <Input
                placeholder="Search by staff name or email"
                value={list.q}
                onChange={e => list.setQ(e.target.value)}
                aria-label="Search the audit log"
              />
            </div>
          </div>

          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr><th>When</th><th>Who</th><th>What</th><th>Record</th><th>IP</th></tr>
                </thead>
                <tbody>
                  {list.loading ? <SkeletonRows rows={8} cols={5} /> : null}
                  {!list.loading && list.items.map(a => (
                    <tr key={a.id}>
                      <td className="dim" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(a.createdAt)}</td>
                      <td>
                        <div style={{ fontWeight: 550 }}>{a.actor.name ?? 'Unknown'}</div>
                        {a.actor.email && a.actor.email !== a.actor.name
                          ? <div className="dim" style={{ fontSize: 'var(--text-xs)' }}>{a.actor.email}</div>
                          : null}
                      </td>
                      <td style={a.action === 'auth.login_denied' ? { color: 'var(--danger)' } : undefined}>
                        {LABEL[a.action] ?? a.action}
                      </td>
                      <td className="muted">{a.entity ?? '—'}</td>
                      <td className="dim mono">{a.ip ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!list.loading && list.items.length === 0 ? (
              <Empty title="Nothing recorded yet">Activity will appear here as staff use the portal.</Empty>
            ) : null}

            {list.error ? (
              <div className="empty">
                <p style={{ color: 'var(--danger)' }}>{list.error}</p>
                <Button onClick={list.refresh} style={{ marginTop: 'var(--s-3)' }}>Try again</Button>
              </div>
            ) : null}
          </div>

          {list.hasMore ? (
            <div className="row" style={{ justifyContent: 'center' }}>
              <Button onClick={list.loadMore} loading={list.loadingMore}>Load more</Button>
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}
